// Firestore REST write helpers extracted from store.js during Step 4.2.
//
// These are the "fire-and-forget" wrappers that:
//   - write localStorage first (instant), then Firestore async
//   - capture failures to Sentry with context
//   - track pending deletes in localStorage so the background sync
//     layer can reconcile them on the next pass
//
// The `desres_pending_deletes` localStorage key format is shared
// between `firestoreDelete` here and `backgroundSyncForDate` in
// store.js — do NOT rename it without updating the consumer.

import * as Sentry from '@sentry/react'
import { restSet, restBatchSet, restDelete } from './firestoreRest'

// Shared localStorage key for the pending-delete queue. Exported so
// the sync layer (store.js backgroundSyncForDate) can read from the
// exact same key.
export const PENDING_DELETES_KEY = 'desres_pending_deletes'

/** Fire-and-forget write to Firestore via REST API. Does nothing
 *  if the record has no `id` (prevents path-slash typos from creating
 *  the implicit sub-collection doc). */
export function firestoreSet(collectionName, record) {
  if (!record || !record.id) return
  restSet(`${collectionName}/${record.id}`, record).catch(err => {
    console.warn(`Firestore write failed (${collectionName}):`, err.message)
    Sentry.captureException(err, { extra: { collection: collectionName, recordId: record.id } })
  })
}

/** Fire-and-forget batch write. Skips records without `id`. */
export function firestoreBatchSet(collectionName, records) {
  if (!records || records.length === 0) return
  restBatchSet(collectionName, records.filter(r => r.id)).catch(err => {
    console.warn(`Firestore batch write failed (${collectionName}):`, err.message)
    Sentry.captureException(err, { extra: { collection: collectionName, count: records.length } })
  })
}

/** Async delete that tracks pending IDs in localStorage. The background
 *  sync layer reads the pending-delete queue and retries on next page
 *  load — this handles offline deletes where the REST call never
 *  reached the server. */
export async function firestoreDelete(collectionName, id) {
  const pending = JSON.parse(localStorage.getItem(PENDING_DELETES_KEY) || '{}')
  if (!pending[collectionName]) pending[collectionName] = []
  if (!pending[collectionName].includes(id)) pending[collectionName].push(id)
  localStorage.setItem(PENDING_DELETES_KEY, JSON.stringify(pending))

  try {
    await restDelete(`${collectionName}/${id}`)
    const p = JSON.parse(localStorage.getItem(PENDING_DELETES_KEY) || '{}')
    if (p[collectionName]) {
      p[collectionName] = p[collectionName].filter(i => i !== id)
      if (p[collectionName].length === 0) delete p[collectionName]
      localStorage.setItem(PENDING_DELETES_KEY, JSON.stringify(p))
    }
  } catch (err) {
    console.warn(`Firestore delete failed (${collectionName}):`, err.message)
    Sentry.captureException(err, { extra: { collection: collectionName, docId: id } })
  }
}

/** Fire-and-forget partial update. Uses PATCH-equivalent through
 *  restSet since Firestore REST merges by default. */
export function firestoreUpdate(collectionName, id, updates) {
  restSet(`${collectionName}/${id}`, updates).catch(err =>
    console.warn(`Firestore update failed (${collectionName}):`, err.message)
  )
}
